import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

export interface OnboardingProgressStep {
  name: string;
  label?: string;
  isCompleted: boolean;
  isActive?: boolean;
  completedAt?: string | null;
  stepNumber?: number;
}

export type OnboardingProgressVariant = 'summary' | 'signing';

@Component({
  selector: 'app-onboarding-progress',
  templateUrl: './onboarding-progress.component.html',
  styleUrls: ['./onboarding-progress.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingProgressComponent {
  @Input() steps: readonly OnboardingProgressStep[] = [];
  @Input() progressLabel = '';
  @Input() progressPercent = 0;
  @Input() currentStepLabel = '';
  @Input() activeStateLabel = '';
  @Input() activeStatusLabel = 'In progress';
  @Input() upcomingStatusLabel = 'Upcoming';
  @Input() showActiveState = true;
  @Input() showDetailsButton = false;
  @Input() showTimeline = true;
  @Input() detailsLabel = 'Card Details';
  @Input() metadataDate: string | null = null;
  @Input() showMetadata = false;
  @Input() ariaLabel = 'Onboarding progress';
  @Input() timelineId: string | null = null;
  @Input() variant: OnboardingProgressVariant = 'summary';

  @Output() detailsClick = new EventEmitter<void>();

  get normalizedProgressPercent(): number {
    const percent = Number(this.progressPercent);
    if (!Number.isFinite(percent)) return 0;
    return Math.min(100, Math.max(0, percent));
  }

  get activeIndex(): number {
    const explicitIndex = this.steps.findIndex(step => step.isActive);
    if (explicitIndex >= 0) return explicitIndex;
    return this.steps.findIndex(step => !step.isCompleted);
  }

  get timelineDateFormat(): string {
    return this.variant === 'signing'
      ? "MMM d, y '•' HH:mm"
      : 'dd MMM yyyy, HH:mm';
  }

  isStepActive(step: OnboardingProgressStep, index: number): boolean {
    return (
      step.isActive === true ||
      (step.isActive !== false && index === this.activeIndex)
    );
  }

  stepNumber(step: OnboardingProgressStep, index: number): number {
    return step.stepNumber ?? index + 1;
  }

  trackByStep(_index: number, step: OnboardingProgressStep): string {
    return step.name;
  }

  onDetailsClick(): void {
    this.detailsClick.emit();
  }
}
